import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ImageIcon, MessageSquareQuote, PenLine } from "lucide-react";
import { splitMessage, tokenizeLine } from "@/lib/messageFormat";

const LINK_CLS = "text-brand underline decoration-brand/40 underline-offset-2 hover:decoration-brand break-words";

function Line({ line }) {
  const segs = tokenizeLine(line.replace(/^>+\s?/, ""));
  return (
    <>
      {segs.map((s, i) => {
        if (s.type === "link") {
          return (
            <a key={i} href={s.href} target={/^mailto:|^tel:/i.test(s.href) ? undefined : "_blank"} rel="noopener noreferrer"
              className={LINK_CLS} title={s.href} data-testid="msg-link">{s.text}</a>
          );
        }
        if (s.type === "image") {
          return (
            <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" title={s.href} data-testid="msg-image-link"
              className="inline-flex items-center gap-1 rounded bg-brema-bg px-1.5 py-0.5 text-[11px] text-muted-foreground hover:text-foreground">
              <ImageIcon className="h-3 w-3" /> Immagine
            </a>
          );
        }
        return <span key={i}>{s.text}</span>;
      })}
    </>
  );
}

function Paragraphs({ text, className = "" }) {
  const lines = (text || "").split("\n");
  return (
    <div className={`break-words ${className}`}>
      {lines.map((ln, i) => (
        ln.trim() ? <p key={i} className="min-h-[1.2em]"><Line line={ln} /></p> : <div key={i} className="h-2" />
      ))}
    </div>
  );
}

function Collapsible({ label, icon: Icon, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 border-t border-border/70 pt-2" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`}
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-brema-bg px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground">
        <Icon className="h-3 w-3" /> {open ? "Nascondi" : "Mostra"} {label}
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {open && <div className="mt-2 border-l-2 border-border pl-3 text-xs text-muted-foreground" data-testid={`${testId}-content`}>{children}</div>}
    </div>
  );
}

// Rende un messaggio (testo o HTML) in modo pulito: corpo, firma/note legali e citazione compresse.
export function MessageBody({ text, className = "", testId = "message-body", collapseQuoted = true, collapseSignature = true }) {
  const parts = useMemo(() => splitMessage(text), [text]);
  const main = parts.main;
  return (
    <div className={`text-sm leading-relaxed text-foreground ${className}`} data-testid={testId}>
      {main ? <Paragraphs text={main} /> : (!parts.signature && !parts.quoted ? <p className="text-muted-foreground">—</p> : null)}
      {parts.signature && (collapseSignature
        ? <Collapsible label="firma e note legali" icon={PenLine} testId={`${testId}-signature`}><Paragraphs text={parts.signature} /></Collapsible>
        : <Paragraphs text={parts.signature} className="mt-2 text-xs text-muted-foreground" />)}
      {parts.quoted && (collapseQuoted
        ? <Collapsible label="messaggio precedente" icon={MessageSquareQuote} testId={`${testId}-quoted`}><Paragraphs text={parts.quoted} /></Collapsible>
        : <Paragraphs text={parts.quoted} className="mt-2 text-xs text-muted-foreground" />)}
    </div>
  );
}

export default MessageBody;
